/**
 * Date helper utilities for formatting and manipulating dates throughout the application
 */

/**
 * Format date to a standard date string (e.g., "Jan 15, 2023")
 * @param {string|Date} dateString - Date object or ISO date string
 * @returns {string} Formatted date string
 */
export const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  
  const options = { 
    year: 'numeric', 
    month: 'short', 
    day: 'numeric' 
  };
  
  try {
    return new Date(dateString).toLocaleDateString(undefined, options);
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Invalid date';
  }
};

/**
 * Format date and time (e.g., "Jan 15, 2023, 3:30 PM")
 * @param {string|Date} dateString - Date object or ISO date string
 * @returns {string} Formatted date and time string
 */
export const formatDateTime = (dateString) => {
  if (!dateString) return 'N/A';
  
  const options = { 
    year: 'numeric', 
    month: 'short', 
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  };
  
  try {
    return new Date(dateString).toLocaleString(undefined, options);
  } catch (error) {
    console.error('Error formatting date and time:', error);
    return 'Invalid date';
  }
};

/**
 * Format time only (e.g., "3:30 PM")
 * @param {string} timeString - Time string in format HH:MM:SS
 * @returns {string} Formatted time string
 */
export const formatTime = (time) => {
  if (!time || time === 'null' || time === 'undefined' || time === '') {
    return null;
  }
  
  try {
    // Handle different time formats
    if (typeof time === 'string') {
      // If it's in HH:MM or H:MM format
      if (time.match(/^\d{1,2}:\d{2}$/)) {
        const [hours, minutes] = time.split(':');
        const hour24 = parseInt(hours);
        const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
        const ampm = hour24 >= 12 ? 'PM' : 'AM';
        return `${hour12}:${minutes} ${ampm}`;
      }
      
      // If it's in HH:MM:SS format
      if (time.match(/^\d{1,2}:\d{2}:\d{2}$/)) {
        const [hours, minutes] = time.split(':');
        const hour24 = parseInt(hours);
        const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
        const ampm = hour24 >= 12 ? 'PM' : 'AM';
        return `${hour12}:${minutes} ${ampm}`;
      }
    }
    
    return null;
  } catch (error) {
    console.warn('Error formatting time:', time, error);
    return null;
  }
};

/**
 * Format a range of time (e.g., "3:30 PM - 4:30 PM")
 * @param {string} startTime - Start time in format HH:MM or HH:MM:SS
 * @param {string} endTime - End time in format HH:MM or HH:MM:SS
 * @returns {string} Formatted time range string
 */
export const formatTimeRange = (startTime, endTime) => {
  const formattedStart = formatTime(startTime);
  const formattedEnd = formatTime(endTime);
  
  if (formattedStart && formattedEnd) {
    return `${formattedStart} - ${formattedEnd}`;
  } else if (formattedStart) {
    return formattedStart;
  } else if (formattedEnd) {
    return `Ends at ${formattedEnd}`;
  }
  
  return null;
};

/**
 * Get relative time (e.g., "2 hours ago", "3 days ago")
 * @param {string|Date} dateString - Date object or ISO date string
 * @returns {string} Relative time string
 */
export const getRelativeTime = (dateString) => {
  if (!dateString) return 'N/A';
  
  try {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    if (diffInSeconds < 60) {
      return 'just now';
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    } else if (diffInSeconds < 2592000) {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days} day${days > 1 ? 's' : ''} ago`;
    } else {
      return formatDate(dateString);
    }
  } catch (error) {
    console.error('Error calculating relative time:', error);
    return 'Invalid date';
  }
};
